import React, { useEffect, useState } from "react";
import { Box, SegmentedControl, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import _ from "lodash";

import RemoteSelect2 from "./remoteSelect2";
import { useServerApi } from "../hooks/userServerApi";
import { linkedClientId } from "../helpers/clientToRelatedPerson";
import { formatPhoneDisplay } from "../helpers/phoneUtils";

const CLIENT_LABEL_RENDER = (data) =>
  `[ ${data.code ?? data.id ?? " -- "} ] ${data.name ?? ""} ${
    data.cname ? `(${data.cname})` : ""
  }`;

const RelatedPersonClientLookup = ({
  form,
  name,
  prefix = "",
  subject = "person",
  disabled = false,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const sourceValue = _.get(form?.values, name);
  const sourceId = linkedClientId(sourceValue);
  const [mode, setMode] = useState(sourceId ? "lookup" : "manual");
  const [preview, setPreview] = useState(
    sourceValue && typeof sourceValue === "object" ? sourceValue : null
  );
  const locked = disabled || readOnly || Boolean(form?.isReadOnly?.());

  const writeLink = (sourceClientId, identityFromClient = sourceClientId != null) => {
    if (!prefix) {
      form.setFieldValue(name, sourceClientId);
      form.setFieldValue("identityFromClient", identityFromClient);
      return;
    }

    const current = _.get(form.values, prefix);
    form.setFieldValue(prefix, {
      ...(current && typeof current === "object" ? current : {}),
      sourceClient: sourceClientId,
      identityFromClient,
    });
  };

  const applyClient = (client) => {
    setPreview(client || null);
    writeLink(client?._id || null, true);
  };

  useEffect(() => {
    if (!sourceId) return undefined;
    if (preview && String(preview._id) === String(sourceId) && preview.name) {
      return undefined;
    }
    if (
      sourceValue &&
      typeof sourceValue === "object" &&
      sourceValue.name &&
      String(sourceValue._id) === String(sourceId)
    ) {
      setPreview(sourceValue);
      return undefined;
    }

    let cancelled = false;
    api
      .getById({ apiEntity: "client", id: sourceId })
      .then((loaded) => {
        if (!cancelled && loaded) setPreview(loaded);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sourceId]);

  const linkedHint =
    subject === "trustee"
      ? t(
          "This trustee stays linked to the client. Client updates show on this order."
        )
      : t(
          "This person stays linked to the client. Client updates show on this order."
        );

  const phone = formatPhoneDisplay(preview?.phone);

  return (
    <Box mb="md">
      <SegmentedControl
        value={mode}
        onChange={(value) => {
          setMode(value);
          if (value === "manual") {
            setPreview(null);
            writeLink(null, false);
            return;
          }
          writeLink(sourceId, true);
        }}
        disabled={locked}
        data={[
          { label: t("Enter manually"), value: "manual" },
          { label: t("Lookup existing client"), value: "lookup" },
        ]}
      />
      {mode === "lookup" && (
        <Box mt="sm">
          <RemoteSelect2
            form={form}
            {...(prefix ? { value: sourceId || null } : { name })}
            label={t("Client")}
            apiEntity="client"
            placeholder="Search Client By ID/ BR / Name "
            valueField="_id"
            labelField="name"
            labelRender={CLIENT_LABEL_RENDER}
            searchFields={["name", "id", "cname", "code", "email"]}
            sort={{ by: "name", order: "asc" }}
            select="name cname code id idType gender email birthday marketingPreference phone phones emails addresses"
            clearable
            zIndex={2000}
            onDataChange={applyClient}
            disabled={locked}
            readOnly={locked}
          />
          {sourceId && preview ? (
            <Box mt={6}>
              <Text size="sm">
                {preview.name || ""}
                {preview.cname ? ` ${preview.cname}` : ""}
              </Text>
              {preview.id ? (
                <Text size="xs" color="dimmed">
                  {preview.id}
                </Text>
              ) : null}
              {phone ? (
                <Text size="xs" color="dimmed">
                  {phone}
                </Text>
              ) : null}
              {preview.email ? (
                <Text size="xs" color="dimmed">
                  {preview.email}
                </Text>
              ) : null}
              <Text size="xs" color="dimmed" mt={4}>
                {linkedHint}
              </Text>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default RelatedPersonClientLookup;
